import React from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { useToggle } from '@/hooks/useToggle'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'
import { AngelPartnerToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/AngelPartnerToolTip'
import { VideoBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/VideoBanner'

interface GiveawayBannerProps {
  theatricalSlug: string
  className?: string
  height?: string
}

export const GuildGiveawayBanner: React.FC<GiveawayBannerProps> = ({ theatricalSlug, className }) => {
  const { t } = useTranslate('tickets')
  const [isYoutubeModalOpen, toggleIsYoutubeModalOpen] = useToggle(false)
  const { isActive: isGiveawayActive } = useGiveawaySchedule(theatricalSlug)

  const supportedSlugs = [slugs.kingOfKings, slugs.theLastRodeo]

  if (!supportedSlugs.includes(theatricalSlug) || !isGiveawayActive) return null

  if (theatricalSlug === slugs.kingOfKings) {
    return (
      <GBFadeInExperiment experimentName="tkok-guild-videobanner" defaultValue={false}>
        <ExperimentVariation variation={true}>
          <VideoBanner
            sources={{
              mobile: '/v1743550697/king-of-kings/tkok-guild-100-entries-banner-2x-mobile-01.webp',
              tablet: '/v1743550697/king-of-kings/tkok-guild-100-entries-banner-2x-tablet-01.webp',
              desktop: '/v1743550697/king-of-kings/tkok-guild-100-entries-banner-2x-desk-01.webp',
            }}
            href={'https://angel.com/giveaway/king-of-kings'}
          />
        </ExperimentVariation>
        <ExperimentVariation default>
          <>
            <div className="relative">
              <div
                className="relative z-[20] mb-4 min-h-[120px] lg:min-h-[100px]"
                onClick={() => toggleIsYoutubeModalOpen()}
              >
                <div
                  className={classNames(
                    'relative bg-cover bg-left bg-no-repeat @sm:max-h-[160px] @2xl:max-h-[120px] bg-[#552960] rounded-[8px] xl:rounded-[12px] lg:rounded-[10px]',
                    '@container flex items-center justify-between @xl:pr-3 h-full',
                    className,
                  )}
                >
                  <FlexColumn className="h-full pl-4 @sm:px-6" style={{ zIndex: 1000 }}>
                    <FlexRow className="gap-1.5">
                      <div className="absolute top-1/2 mx-auto w-fit -translate-y-1/2" style={{ zIndex: 1000 }}>
                        <Text className="font-weight-[375] relative !font-whitney text-[14px] leading-[20px] text-white sm:text-[16px] ">
                          <Translate i18nKey="GuildGiveawayGet100Entriesv2" t={t}>
                            Guild members get 100 giveaway entries!
                          </Translate>
                        </Text>
                        <FlexRow className="gap-12 leading-3">
                          <Text
                            weight="bold"
                            className="photon-title-sm md:!photon-heading-xs block text-center text-[20px] !text-[#F1B420] sm:text-[24px] 2xl:text-[28px]"
                          >
                            {t('tkokClaimGuildTickets', 'Claim Your Guild Tickets')}
                          </Text>
                          <div className="hidden @2xl:block">
                            <AngelPartnerToolTip
                              className="mt-1"
                              buttonClassName="bg-white/20 border-none text-white focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent backdrop-blur-[24px] !backdrop-filter"
                            />
                          </div>
                        </FlexRow>
                        <Link
                          href={'/giveaway/king-of-kings'}
                          target="_blank"
                          className="text-[10px] text-white underline sm:text-[14px]"
                        >
                          Learn more about The King of Kings Giveaway.
                        </Link>
                        <div className="pt-1 @2xl:hidden">
                          <AngelPartnerToolTip
                            className="mt-1"
                            buttonClassName="bg-white/20 border-none text-white focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent backdrop-blur-[24px] !backdrop-filter"
                          />
                        </div>
                      </div>
                    </FlexRow>
                  </FlexColumn>

                  <div
                    className={classNames(
                      'absolute right-[133px] top-0 z-10 hidden h-full w-[120px] bg-gradient-to-l from-transparent',
                      `to-[#552960]`,
                      'to-35% @2xl:block',
                    )}
                  />
                  <div
                    className={classNames(
                      'absolute right-0 top-0 z-10  h-full w-[100%] bg-gradient-to-l from-transparent rounded-[8px] xl:rounded-[12px] lg:rounded-[10px]',
                      `to-[#552960]`,
                      'to-35% @2xl:hidden',
                    )}
                  />
                  <div className="relative h-full overflow-hidden rounded-r-[8px] lg:rounded-r-[10px] xl:rounded-r-[12px]">
                    <div className="z-1 mr-0 hidden @2xl:block lg:-mr-3">
                      <Image
                        src={
                          '/c_fill,w_400,h_200,f_webp,q_auto/v1742574360/king-of-kings/ebe2e2c3aab5c8b956b36ca41cb991ce.jpg'
                        }
                        alt={'the king of kings giveaway'}
                        width={200}
                        height={200}
                      />
                    </div>
                    <div className="z-1 -mr-10 @2xl:hidden">
                      <Image
                        src={
                          '/c_fill,w_400,h_200,f_webp,q_auto/v1742574360/king-of-kings/ebe2e2c3aab5c8b956b36ca41cb991ce.jpg'
                        }
                        alt={'the king of kings giveaway'}
                        width={260}
                        height={200}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {theatricalSlug === slugs.kingOfKings && (
              <GBFadeInExperiment experimentName="king-of-kings-video" defaultValue={false}>
                <ExperimentVariation variation={true}>
                  <YoutubeVideoModal
                    youTubeUrl={'https://www.youtube-nocookie.com/embed/tOudE4H1kvM'}
                    showModal={isYoutubeModalOpen}
                    handleClose={() => toggleIsYoutubeModalOpen(false)}
                    className="!z-[60]"
                  />
                </ExperimentVariation>
                <ExperimentVariation default>
                  <YoutubeVideoModal
                    youTubeUrl={'https://www.youtube-nocookie.com/embed/85jmrZfkghE'}
                    showModal={isYoutubeModalOpen}
                    handleClose={() => toggleIsYoutubeModalOpen(false)}
                    className="!z-[60]"
                  />
                </ExperimentVariation>
              </GBFadeInExperiment>
            )}
          </>
        </ExperimentVariation>
      </GBFadeInExperiment>
    )
  }
  if (theatricalSlug === slugs.theLastRodeo) {
    return (
      <>
        <div className="relative">
          <div
            className="relative z-[20] mb-4 min-h-[120px] lg:min-h-[100px]"
            onClick={() => toggleIsYoutubeModalOpen()}
          >
            <div
              className={classNames(
                'relative  bg-cover bg-left bg-no-repeat @sm:max-h-[160px] @2xl:max-h-[120px] bg-[#E5DFD3] rounded-[8px] xl:rounded-[12px] lg:rounded-[10px]',
                '@container flex items-center justify-between @xl:pr-3 h-full',
                className,
              )}
            >
              <FlexColumn className="h-full pl-4 @sm:px-6" style={{ zIndex: 1000 }}>
                <FlexRow className="gap-1.5">
                  <div className="absolute top-1/2 mx-auto w-fit -translate-y-1/2" style={{ zIndex: 1000 }}>
                    <Text className="font-weight-[375] relative !font-whitney text-[14px] leading-[20px] text-black sm:text-[16px] ">
                      <Translate i18nKey="GuildGiveawayGet100Entriesv2" t={t}>
                        Guild members get 100 giveaway entries!
                      </Translate>
                    </Text>
                    <FlexRow className="gap-12 leading-3">
                      <Text
                        weight="bold"
                        className="photon-title-sm md:!photon-heading-xs block text-center text-[20px] !text-[#D7300F] sm:text-[24px] 2xl:text-[28px]"
                      >
                        {t('tkokClaimGuildTickets', 'Claim Your Guild Tickets')}
                      </Text>
                      <div className="hidden @2xl:block">
                        <AngelPartnerToolTip
                          className="mt-1"
                          buttonClassName="bg-black/20 border-none text-black focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent backdrop-blur-[24px] !backdrop-filter"
                        />
                      </div>
                    </FlexRow>
                    <Link
                      href={'/giveaway/last-rodeo'}
                      target="_blank"
                      className="text-[10px] text-black underline sm:text-[14px]"
                    >
                      Learn more about The Last Rodeo Giveaway.
                    </Link>
                    <div className="pt-1 @2xl:hidden">
                      <AngelPartnerToolTip
                        className="mt-1"
                        buttonClassName="bg-black/20 border-none text-black focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent backdrop-blur-[24px] !backdrop-filter"
                      />
                    </div>
                  </div>
                </FlexRow>
              </FlexColumn>

              <div
                className={classNames(
                  'absolute right-[133px] top-0 z-10 hidden h-full w-[120px] bg-gradient-to-l from-transparent',
                  `to-[#E5DFD3]`,
                  'to-35% @2xl:block',
                )}
              />
              <div
                className={classNames(
                  'absolute right-0 top-0 z-10  h-full w-[100%] bg-gradient-to-l from-transparent',
                  `to-[#E5DFD3]`,
                  'to-35% @2xl:hidden',
                )}
              />
              <div className="relative h-full overflow-hidden">
                <div className="z-1 mr-0 hidden @2xl:block lg:-mr-3">
                  <Image
                    src={'/c_fill,w_400,h_200,f_webp,q_auto/v1743121100/the-last-rodeo/truck-trailer-sm.webp'}
                    alt={'the king of kings giveaway'}
                    width={200}
                    height={200}
                  />
                </div>
                <div className="z-1 -mr-10 @2xl:hidden">
                  <Image
                    src={'/c_fill,w_400,h_200,f_webp,q_auto/v1743121100/the-last-rodeo/truck-trailer-sm.webp'}
                    alt={'the king of kings giveaway'}
                    width={260}
                    height={200}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {theatricalSlug === slugs.theLastRodeo && (
          <YoutubeVideoModal
            youTubeUrl={'https://www.youtube-nocookie.com/embed/8dC_GBIosa4'}
            showModal={isYoutubeModalOpen}
            handleClose={() => toggleIsYoutubeModalOpen(false)}
            className="!z-[60]"
          />
        )}
      </>
    )
  }
}
